import React from 'react';
import moment from 'moment-timezone';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import CardLayout from '@magoosh/layouts/card';
import LessonActivity from '@magoosh/organisms/activities/lesson';
import ScoreReportStatusCard from '@magoosh/organisms/score_report_status_card';
import RatingForm from '@magoosh/organisms/rating';
import { paths } from 'config/path_helpers';

import ContextualLink from '@test_prep/app/components/contextual_link';
import { useAppContext } from '@test_prep/app/context';
import { useQueries } from '@test_prep/app/queries';

import EditedResponseSection from './sections/edited_response_section';
import EssayPromptAndResponseSection from './sections/essay_prompt_and_response_section';
import FeedbackSection from './sections/feedback_section';
import ScoreCard from './components/score_card';
import SpeakingResponsesSection from './sections/speaking_responses_section';

const ShowScoreReport: React.FC<{}> = (props) => {
  const { branding } = useAppContext().data;
  const { id } = useParams();
  const q = useQueries();

  const pagePath = paths.scoreReport(id);
  const pageQuery = q.pageQuery(pagePath);
  const page = pageQuery.data;

  const hasMultipleResponses = page && page.scoreReport.responses.length > 1;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const breadcrumbs = () => {
   if (page?.practiceSession?.practiceTargetType === 'MockTest') {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Practice Tests', path: paths.practiceTests() },
        { name: 'Results', path: paths.practiceTest(page.practiceSession.practiceTargetId) },
        { name: 'Score Report' }
      ];
    } else if (page?.practiceSession) {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Practice Session', path: paths.reviewAnswers({ 'filters[practice_session_id]': page.practiceSession.id }) },
        { name: 'Score Report' }
      ];
    } else {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Score Report' }
      ];
    }
  };

  const handleScoreReportFinished = () => {
    pageQuery.refetch();
  };

  const lessonsWatchedCount = () => {
    return page.relatedLessons.filter((lesson) => (
      (lesson as any).status.video === 'watched' || (lesson as any).status.video === 'completed'
    )).length;
  };

  if (!page) {
    return (
      <div style={{ position: 'relative' }}>
        <Helmet>
          <title>Score Report</title>
        </Helmet>

        <CardLayout breadcrumbs={breadcrumbs()} loading />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Helmet>
        <title>
          {
            page.scoreReport.reportType ? (
              `${page.scoreReport.reportType} Score Report`
            ) : 'Score Report'
          }
        </title>
      </Helmet>

      <CardLayout breadcrumbs={breadcrumbs()} linkComponent={ContextualLink}>
        <div className="d-flex flex-column gap-m">
          {
            page.scoreReport.status !== 'finished' && (
              <ScoreReportStatusCard
                answer={{
                  ...page.scoreReport.responses[0].answer,
                  scoreReport: page.scoreReport
                }}
                onFinished={handleScoreReportFinished}
              />
            )
          }

          <div className="d-flex flex-column flex-lg-row gap-s">
            <div className="d-flex flex-column gap-s flex-1">
              <h1 className="text-gray-darker u-margin-A-n">
                <strong>
                  {
                    page.scoreReport.reportType ? (
                      `${page.scoreReport.reportType} Score Report`
                    ) : 'Score Report'
                  }
                </strong>
              </h1>

              <div className="d-flex flex-column gap-xs">
                {
                  page.practiceSession?.practiceTargetType === 'MockTest' ? (
                    <>
                      <div>
                        This response was part of a practice test you completed <strong>{
                          moment(page.practiceSession.finishedAt * 1000).tz(timeZone).isSame(moment().tz(timeZone), 'day')
                            ? 'Today'
                            : moment(page.practiceSession.finishedAt * 1000).tz(timeZone).fromNow()
                        }</strong>
                      </div>

                      <div>
                        <ContextualLink
                          className="text-secondary text-hover-no-decoration text-no-decoration"
                          href={paths.practiceTest(page.practiceSession.practiceTargetId)}
                        >
                          <strong>See full practice test score report</strong>
                        </ContextualLink>
                      </div>
                    </>
                  ) : (
                    <div>
                      Answered <strong>{
                        moment(page.scoreReport.responses[0].answer.createdAt * 1000).tz(timeZone).isSame(moment().tz(timeZone), 'day')
                          ? 'Today'
                          : moment(page.scoreReport.responses[0].answer.createdAt * 1000).tz(timeZone).fromNow()
                      }</strong>
                    </div>
                  )
                }
              </div>
            </div>

            <ScoreCard
              hint={hasMultipleResponses ? 'This is your average score across all responses.' : null}
              previousScore={page.previousScore}
              score={page.scoreReport.score}
              scoreRange={page.scoreReport.scoreRange}
              status={page.scoreReport.status}
              title={hasMultipleResponses ? 'Overall Score' : 'Score'}
            />
          </div>

          {
            page.requestType === 'essay' && (
              <>
                <EssayPromptAndResponseSection answer={...page.scoreReport.responses[0].answer} />
                <FeedbackSection
                  examName={branding.examName}
                  requestType={page.requestType}
                  scoreReport={page.scoreReport}
                />
              </>
            )
          }

          {
            page.requestType === 'speaking' && (
              <>
                <SpeakingResponsesSection responses={page.scoreReport.responses} />

                <hr className="u-margin-A-n" />
                
                <FeedbackSection
                  examName={branding.examName}
                  requestType={page.requestType}
                  scoreReport={page.scoreReport}
                  title="Your overall score explained"
                />
              </>
            )
          }

          {
            page.requestType === 'essay' && (
              <EditedResponseSection
                examName={branding.examName}
                scoreReport={page.scoreReport}
              />
            )
          }

        <div className="d-flex flex-column gap-m u-margin-T-m">
          <div className="d-flex flex-column gap-s gap-lg-m">

            <div className="align-items-center d-flex gap-s">
              <h3 className="u-margin-A-n"><strong>Tell Us How We Did</strong></h3>
            </div>

            <hr className="u-margin-A-n" />
          </div>

          <RatingForm
            rating={page.rating}
            rateableName="score report and feedback"
          />
        </div>

        {
          page.relatedLessons.length > 0 && (
            <div className="d-flex flex-column gap-m u-margin-T-m">
              <div className="d-flex flex-column gap-s gap-lg-m">

                <div className="align-items-center d-flex gap-s">
                  <h3 className="u-margin-A-n"><strong>Lessons</strong></h3>

                  <div style={{ fontSize: 18}}>
                    ({lessonsWatchedCount()} of {page.relatedLessons.length} watched)
                  </div>
                </div>

                <hr className="u-margin-A-n" />
              </div>

              <div className="d-flex flex-column gap-m">
                {
                  page.relatedLessons.map((lesson, idx) => (
                    <CardLayout.Card key={idx} className="flex-1">
                      <LessonActivity {...lesson} />
                    </CardLayout.Card>
                  ))
                }
              </div>
            </div>
          )
        }
        </div>
      </CardLayout>
    </div>
  );
};

export default ShowScoreReport;
